import { DOCUMENT, Location, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    LOCALE_ID,
    PLATFORM_ID,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, Event as RouterEvent, RouterOutlet } from '@angular/router';

import { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

import { BlockDirective, ElemDirective } from '@rt-tools/core';
import { RtToasterComponent } from '@rt-tools/ui-kit-v2';
import { AnalyticsService, ConsentService, EAnalyticsGoal } from '@stair-craft/common/analytics';
import { AppFooterComponent, AppHeaderComponent, IAppLocale, SiteHeaderStateService } from '@stair-craft/common/components';
import { CookieBannerComponent } from '@stair-craft/common/components';
// A path of its own: the chat is deferred, and the barrel already lies in the initial bundle
import { VisitorChatComponent } from '@stair-craft/common/components/visitor-chat';
import { IStairType, SeoService } from '@stair-craft/common/util';
import { buildLocaleHref } from '@stair-craft/common/util';

const CALC_URL_PREFIX: string = '/calc';
const ORDER_URL_PREFIX: string = '/order';
const NO_SCROLL_BODY_CLASS: string = 'sc-no-scroll';

const STAIR_PATH_TO_TYPE: Readonly<Record<string, IStairType>> = {
    straight: 'straight',
    l: 'l-shape',
    u: 'u-shape',
    spiral: 'spiral',
};

// Parses the stair type out of a calc-route URL. Returns null for any URL that
// isn't `/calc/<type>` — used by the header to highlight the active link even
// though the constructor updates the address bar via `Location.replaceState`
// (which does not fire Router NavigationEnd events).
function parseStairTypeFromUrl(url: string): IStairType | null {
    const match: RegExpExecArray | null = /^\/calc\/(straight|l|u|spiral)(?:[/?#]|$)/.exec(url);
    return match ? STAIR_PATH_TO_TYPE[match[1]] : null;
}

const BEM_BLOCK: string = 'sc-app-shell';

@Component({
    selector: 'sc-app-shell',
    templateUrl: './app-shell.component.html',
    styleUrl: './app-shell.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // angular
        RouterOutlet,

        // rt-tools
        BlockDirective,
        ElemDirective,

        // shared-ui-layout
        AppHeaderComponent,
        AppFooterComponent,

        // shared-ui-overlays
        CookieBannerComponent,
        VisitorChatComponent,

        // rt-tools ui-kit
        RtToasterComponent,
    ],
    host: {
        class: BEM_BLOCK,
        '[class.sc-app-shell--calc]': 'isCalcRoute()',
    },
})
export default class AppShellComponent {
    readonly #router: Router = inject(Router);
    readonly #location: Location = inject(Location);
    readonly #analytics: AnalyticsService = inject(AnalyticsService);
    readonly #consent: ConsentService = inject(ConsentService);
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #doc: Document = inject(DOCUMENT);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);
    readonly #headerState: SiteHeaderStateService = inject(SiteHeaderStateService);

    readonly #activeStairType: WritableSignal<IStairType | null> = signal<IStairType | null>(parseStairTypeFromUrl(this.#location.path()));
    // Path with query of the page on screen; the switcher links lead to this same page in the other language.
    readonly #currentPath: WritableSignal<string> = signal<string>(this.#location.path() || '/');

    protected readonly currentLocale: string = inject(LOCALE_ID);
    protected readonly ruHref: Signal<string> = computed((): string =>
        buildLocaleHref('ru', this.#currentPath(), this.#currentAppLocale())
    );
    protected readonly enHref: Signal<string> = computed((): string =>
        buildLocaleHref('en', this.#currentPath(), this.#currentAppLocale())
    );

    protected readonly isCalcRoute: Signal<boolean> = this.#routePrefixSignal(CALC_URL_PREFIX);
    protected readonly isOrderRoute: Signal<boolean> = this.#routePrefixSignal(ORDER_URL_PREFIX);
    protected readonly activeStairType: Signal<IStairType | null> = this.#activeStairType.asReadonly();
    protected readonly headerOverVideo: Signal<boolean> = this.#headerState.overVideo.asReadonly();
    protected readonly headerOverlay: Signal<boolean> = this.#headerState.overlay.asReadonly();

    constructor() {
        // Instantiate the root SeoService for its side effect: its constructor
        // subscribes to router events and applies SEO on every navigation.
        inject(SeoService);

        effect((): void => {
            const body: HTMLElement | null = (this.#doc as { body?: HTMLElement | null }).body ?? null;
            if (!body) {
                return;
            }
            body.classList.toggle(NO_SCROLL_BODY_CLASS, this.isCalcRoute());
        });

        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }

        // `Location.onUrlChange` fires for `replaceState` too — the constructor
        // page swaps `/calc/straight` ↔ `/calc/l` via replaceState (to preserve
        // the lazy component instance), and Router NavigationEnd does not fire
        // on that. This keeps the header's active link in sync regardless.
        const unlisten: VoidFunction = this.#location.onUrlChange((url: string): void => {
            this.#activeStairType.set(parseStairTypeFromUrl(url));
            this.#currentPath.set(this.#location.path() || '/');
        });
        this.#destroyRef.onDestroy((): void => {
            unlisten();
        });

        this.#analytics.bootstrap();

        this.#router.events
            .pipe(
                filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed()
            )
            .subscribe((event: NavigationEnd): void => {
                this.#analytics.trackPageView(event.urlAfterRedirects);
            });

        this.#consent.hydrate();
    }

    // The link itself navigates: its address already leads to the same page in the other language.
    protected onLocaleSelected(target: IAppLocale): void {
        this.#analytics.trackGoal(EAnalyticsGoal.LocaleSwitch, { from: this.currentLocale, to: target });
    }

    #currentAppLocale(): IAppLocale {
        return this.currentLocale === 'en' ? 'en' : 'ru';
    }

    #routePrefixSignal(prefix: string): Signal<boolean> {
        const source: Observable<boolean> = this.#router.events.pipe(
            filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
            map((event: NavigationEnd): boolean => event.urlAfterRedirects.startsWith(prefix)),
            startWith(this.#router.url.startsWith(prefix))
        );
        return toSignal(source, { initialValue: false });
    }
}
